import { useBillingServiceStore } from '@billing/AppProvider'
import { HandlingServicesPrebillingDto } from '@billing/app/api'
import ReportStep from '@billing/app/models/report-step.enum'
import { Box, CircularProgress, Stack } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  TomTable,
  tomTableColumnFilterPresets,
  useDefaultMRTOptions,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import {
  exportToCsv,
  IDataExportConfig,
} from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { useEffect, useMemo } from 'react'
import { HandlingPreBillingReportFilter } from '../components/handlingPreBillingReport/HandlingPreBillingReportFilter'
import { ReportNavigationButtonContainer } from './ReportNavigationButtonContainer'

export const HandlingServicesContainer = observer(() => {
  const { reportsUIStore, customerBillingUIStore, handlingServicesPrebillingUIStore } =
    useBillingServiceStore()
  const { t } = useTranslate()

  const dataExportConfig = useMemo<IDataExportConfig<any>>(
    () => ({
      createdDate: { header: t('createdDate', 'Created Date'), type: 'string' },
      closedDate: { header: t('closedDate', 'Closed Date'), type: 'string' },
      customer: { header: t('customer', 'Customer'), type: 'string' },
      referenceNumber: { header: t('referenceNumber', 'Reference number'), type: 'string' },
      commodity: { header: t('commodity', 'Commodity'), type: 'string' },
      carrier: { header: t('carrier', 'Carrier'), type: 'string' },
      handlingDirection: { header: t('direction', 'Direction'), type: 'string' },
      carrierType: { header: t('carrierType', 'Carrier Type'), type: 'string' },
      lotNumber: { header: t('lotNumber', 'Lot'), type: 'string' },
      plannedQuantity: { header: t('quantity', 'Qty'), type: 'number' },
      grossWeight: { header: t('grossWeight', 'Weight'), type: 'number' },
      isDangerousGoods: { header: t('dangerous', 'Dangerous'), type: 'string' },
      weightUnit: { header: t('unit', 'Unit'), type: 'string' },
    }),
    [t],
  )

  const dataExport = {
    title: t('handlingServices', 'Handling services'),
    enableDataExport: true,
    dataExportConfig,
  }

  const columns = useMemo<MRT_ColumnDef<HandlingServicesPrebillingDto>[]>(
    () => [
      {
        id: 'createdDate',
        header: t('createdDate', 'Created Date'),
        accessorKey: 'createdDate',
        Cell: ({ cell }) => {
          const val = cell.getValue<string>()
          return val ? dayjs(val).format('DD/MM/YYYY') : ''
        },
        size: 110,
        ...tomTableColumnFilterPresets.dateOptional(),
      },
      {
        id: 'closedDate',
        header: t('closedDate', 'Closed Date'),
        accessorKey: 'closedDate',
        Cell: ({ cell }) => {
          const val = cell.getValue<string>()
          return val ? dayjs(val).format('DD/MM/YYYY') : ''
        },
        size: 110,
        ...tomTableColumnFilterPresets.dateOptional(),
      },
      {
        id: 'referenceNumber',
        header: t('referenceNumber', 'Reference number'),
        accessorKey: 'referenceNumber',
        size: 200,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'commodity',
        header: t('commodity', 'Commodity'),
        accessorKey: 'commodity',
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'carrier',
        header: t('carrier', 'Carrier'),
        accessorKey: 'carrier',
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'handlingDirection',
        header: t('direction', 'Direction'),
        accessorKey: 'handlingDirection',
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'carrierType',
        header: t('carrierType', 'Carrier type'),
        accessorKey: 'carrierType',
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'lotNumber',
        header: t('lotNumber', 'Lot number'),
        accessorKey: 'lotNumber',
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'plannedQuantity',
        header: t('quantity', 'Quantity'),
        accessorKey: 'plannedQuantity',
        size: 80,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'grossWeight',
        header: t('grossWeight', 'Gross weight'),
        accessorKey: 'grossWeight',
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'isDangerousGoods',
        header: t('dg', 'DG'),
        accessorFn: row => (row.isDangerousGoods ? t('yes', 'Yes') : t('no', 'No')),
        size: 80,
      },
      {
        id: 'weightUnit',
        header: t('unit', 'Unit'),
        accessorKey: 'weightUnit',
        ...tomTableColumnFilterPresets.searchOptional(),
      },
    ],
    [t],
  )

  const defaultOptions = useDefaultMRTOptions<HandlingServicesPrebillingDto>(undefined, dataExport)

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'billing/handling-services',
  )

  const table = useTomTable(
    {
      columns,
      data: handlingServicesPrebillingUIStore.handlingData,
      enableRowSelection: false,
      enableTopToolbar: false,
      enableExpanding: false,
      enableRowActions: false,
      initialState: {
        ...defaultOptions.initialState,
        sorting: [{ id: 'createdDate', desc: false }],
      },
      muiTableContainerProps: {
        sx: {
          minHeight: '450px',
        },
      },
    },
    undefined,
    dataExport,
    { columns, savedPreferences, onPreferencesChange },
  )

  const handleDownload = () => {
    const rows = table.getPrePaginationRowModel().rows.map(r => ({
      ...r.original,
      isDangerousGoods: r.original.isDangerousGoods ? t('yes') : t('no'),
    }))

    exportToCsv(
      t('handlingReport', 'Handling Services Report'),
      rows,
      dataExportConfig,
      `Handling_${dayjs().format('YYYYMMDD')}.csv`,
    )
  }

  useEffect(() => {
    if (reportsUIStore.currentStep === ReportStep.Report) {
      handlingServicesPrebillingUIStore.fetchHandlingData()
    }
  }, [handlingServicesPrebillingUIStore, reportsUIStore.currentStep])

  const finish = () => {
    handlingServicesPrebillingUIStore.resetFilter()
    customerBillingUIStore.resetData()
    reportsUIStore.resetStepper()
  }

  return (
    <>
      {reportsUIStore.currentStep === ReportStep.Filter && <HandlingPreBillingReportFilter />}

      {reportsUIStore.currentStep === ReportStep.Report && (
        <Box sx={{ width: '100%', mt: 1 }}>
          {handlingServicesPrebillingUIStore.isLoading ? (
            <Stack alignItems='center' py={5}>
              <CircularProgress />
            </Stack>
          ) : (
            <TomTable table={table} />
          )}
        </Box>
      )}

      <ReportNavigationButtonContainer
        disableNext={!handlingServicesPrebillingUIStore.customerId}
        downloadAction={handleDownload}
        finishAction={finish}
      />
    </>
  )
})
